<script lang="ts">
  /**
   * Forge endpoints — operator-loaded Neon rows.
   * Use / Wake / Sleep only. Customers do not create iron here.
   */
  import { onDestroy, onMount } from "svelte";
  import type { GpuPluginHost } from "../host.js";
  import { toast } from "../host.js";
  import type { Connection } from "../connections.js";
  import {
    fetchSuiteConnections,
    selectSuiteConnection,
    sleepForgeEndpoint,
    wakeForgeEndpoint,
    wakeMusic3Endpoint,
    fetchMusic3Wake,
  } from "../suite-client.js";
  import { fetchDumpCatalog } from "../client.js";
  import { dumpRowsToForgeConnections } from "../catalog.js";
  import { uniqueBy } from "../utils.js";

  interface Props {
    host: GpuPluginHost;
  }

  let { host }: Props = $props();

  let rows = $state<Connection[]>([]);
  let selectedId = $state<string | null>(null);
  let loading = $state(true);
  let loadError = $state<string | null>(null);
  let busy = $state(false);
  let timer: ReturnType<typeof setInterval> | null = null;

  function client() {
    return {
      forgeApiUrl: host.forgeApiUrl,
      getAccessToken: host.getAccessToken,
      appId: host.appId,
      toolId: host.accessHubTool,
      dumpCatalogUrl: host.dumpCatalogUrl,
    };
  }

  async function load(force = false) {
    loading = rows.length === 0;
    try {
      const opts = client();
      const [dumped, data] = await Promise.all([
        host.appId
          ? fetchDumpCatalog(opts, "forge_endpoint", {
              app: host.appId,
              tool: host.accessHubTool,
            })
          : Promise.resolve(null),
        fetchSuiteConnections(opts, {
          kind: "forge_endpoint",
          app: host.appId,
          force,
        }),
      ]);
      const origin = uniqueBy(data.connections || [], (r) => r.id);
      const all = dumpRowsToForgeConnections(dumped?.models || [], origin);
      if (host.purpose === "music") {
        const music = all.filter((r) =>
          /music3|minimax|text-to-music|\bttm\b/i.test(
            `${r.modelId || ""} ${r.label || ""} ${r.engine || ""} ${r.modality || ""}`,
          ),
        );
        rows = music.length ? music : all;
      } else {
        rows = all;
      }
      loadError = null;
      const pref = host.purpose ? data.selectedByPurpose?.[host.purpose] : undefined;
      if (pref && rows.some((r) => r.id === pref)) selectedId = pref;
    } catch (e) {
      loadError = e instanceof Error ? e.message : String(e);
    } finally {
      loading = false;
    }
  }

  onMount(() => {
    void load();
    timer = setInterval(() => void load(), 10_000);
    return () => {
      if (timer) clearInterval(timer);
    };
  });

  onDestroy(() => {
    if (timer) clearInterval(timer);
  });

  const selected = $derived(rows.find((r) => r.id === selectedId) ?? null);
  const statusOf = (row: Connection | null) => (row?.status || "").toLowerCase();
  const isWakeable = (row: Connection | null) => {
    const s = statusOf(row);
    return (
      s === "sleeping" ||
      s === "error" ||
      s === "host_lost" ||
      s === "stopped" ||
      s === "paused" ||
      s === "idle" ||
      s.includes("scaledtoz") ||
      s.includes("scale-to-zero") ||
      s.includes("scaled_to_zero")
    );
  };
  const canUse = $derived(Boolean(selected?.ready || isWakeable(selected)));
  const canWake = $derived(isWakeable(selected));
  const pending = $derived(
    selected
      ? ["creating", "pulling", "starting", "provisioning", "loading", "updating", "waking", "pending"].includes(
          statusOf(selected),
        )
      : false,
  );

  function looksMusic3(row: Connection | null) {
    if (!row) return false;
    return /music3|minimax-music|text-to-music/i.test(
      `${row.modelId || ""} ${row.label || ""} ${row.sourceId || ""} ${row.engine || ""} ${row.modality || ""}`,
    );
  }

  async function kickWake(row: Connection) {
    if (looksMusic3(row) || host.purpose === "music") {
      try {
        const snap = await fetchMusic3Wake(client());
        const phase = String(snap?.phase || snap?.code || "").toLowerCase();
        if (phase === "ready") return;
      } catch {
        /* fall through to kick */
      }
      await wakeMusic3Endpoint(client(), {
        endpoint: row.endpointUrl || undefined,
        name: row.sourceId || row.label || undefined,
      });
      return;
    }
    const id = row.sourceId || row.id;
    await wakeForgeEndpoint(client(), id);
  }

  async function useRow(row: Connection) {
    if (!row.ready && !isWakeable(row)) return;
    busy = true;
    try {
      if (!row.ready || isWakeable(row)) {
        await kickWake(row);
        toast(host, "info", "Waking GPU — first generate may take a minute");
      }
      if (host.appId && host.purpose) {
        await selectSuiteConnection(client(), {
          appId: host.appId,
          purpose: host.purpose,
          connectionId: row.id,
        });
      }
      await host.onConnect?.(row);
      toast(host, "success", `Using ${row.label}`);
      await load(true);
    } catch (e) {
      toast(host, "error", e instanceof Error ? e.message : String(e));
    } finally {
      busy = false;
    }
  }

  async function wake(row: Connection) {
    busy = true;
    try {
      await kickWake(row);
      if (host.appId && host.purpose) {
        await selectSuiteConnection(client(), {
          appId: host.appId,
          purpose: host.purpose,
          connectionId: row.id,
        });
      }
      toast(host, "info", "Waking endpoint — first generate may take a minute");
      await load(true);
    } catch (e) {
      toast(host, "error", e instanceof Error ? e.message : String(e));
    } finally {
      busy = false;
    }
  }

  async function sleep(row: Connection) {
    busy = true;
    try {
      await sleepForgeEndpoint(client(), row.sourceId || row.id);
      toast(host, "info", "Sleep requested");
      await load(true);
    } catch (e) {
      toast(host, "error", e instanceof Error ? e.message : String(e));
    } finally {
      busy = false;
    }
  }
</script>

<div class="fgp-col">
  <div class="fgp-top">
    <h2>Forge endpoints</h2>
    <span class="fgp-top-meta">Loaded by Forge · Use / Wake / Sleep</span>
    <div class="fgp-top-actions">
      <button type="button" class="fgp-btn fgp-btn-ghost" onclick={() => void load(true)}>Refresh</button>
    </div>
  </div>

  {#if loadError}
    <div class="fgp-alert">{loadError}</div>
  {/if}

  <div class="fgp-grid">
    <div class="fgp-panel">
      <div class="fgp-panel-head">
        <p class="fgp-muted" style="margin:0">
          Endpoints Forge has already loaded. Wake a sleeper, then Use.
        </p>
      </div>
      <div class="fgp-panel-body">
        {#each rows as row (row.id)}
          <div class="fgp-row" data-active={selectedId === row.id} data-tone="sky">
            <button
              type="button"
              class="fgp-row-main"
              onclick={() => {
                selectedId = row.id;
                if (host.purpose === "chat") void useRow(row);
              }}
            >
              <div style="min-width:0;flex:1">
                <div class="fgp-row-title">
                  <span class="name">{row.label}</span>
                  <span class="fgp-badge-mod">{row.status}</span>
                  {#if row.skuId}<span class="fgp-badge-mod">{row.skuId}</span>
                  {:else if row.engine}<span class="fgp-badge-mod">{row.engine}</span>{/if}
                </div>
                <div class="fgp-row-sub">{row.modelId || row.endpointUrl || ""}</div>
              </div>
            </button>
          </div>
        {:else}
          <p class="fgp-empty">
            {loading ? "Loading…" : "No Forge endpoints are online for this app yet."}
          </p>
        {/each}
      </div>
    </div>

    <div class="fgp-panel">
      {#if !selected}
        <div class="fgp-empty" style="flex:1;display:flex;align-items:center;justify-content:center">
          Pick an endpoint
        </div>
      {:else}
        <div class="fgp-panel-side">
          <h3 class="fgp-detail-title">{selected.label}</h3>
          <p class="fgp-row-sub" style="margin:0">{selected.modelId}</p>
          <p class="fgp-muted" style="margin:0">
            {selected.status}
            {#if selected.tokenLast4} · bind_…{selected.tokenLast4}{/if}
          </p>
          {#if selected.detail}
            <p class="fgp-muted" style="margin:0">{selected.detail}</p>
          {/if}
          {#if pending}
            <p class="fgp-muted" style="margin:0">Waking — first generate may take a minute.</p>
          {/if}
        </div>
        <div class="fgp-panel-foot">
          {#if pending}
            <button type="button" class="fgp-btn fgp-btn-primary" disabled>
              Waking…
            </button>
          {:else if canWake}
            <button type="button" class="fgp-btn fgp-btn-primary" disabled={busy} onclick={() => void wake(selected)}>
              {statusOf(selected) === "error" || statusOf(selected) === "host_lost" ? "Retry" : "Wake"}
            </button>
            <button
              type="button"
              class="fgp-btn"
              disabled={busy}
              onclick={() => void useRow(selected)}
            >
              Use endpoint
            </button>
          {:else}
            <button
              type="button"
              class="fgp-btn fgp-btn-primary"
              disabled={!canUse || busy}
              onclick={() => void useRow(selected)}
            >
              Use endpoint
            </button>
          {/if}
          {#if selected.ready}
            <button type="button" class="fgp-btn" disabled={busy} onclick={() => void sleep(selected)}>
              Sleep
            </button>
          {/if}
        </div>
      {/if}
    </div>
  </div>
</div>
